import React from "react";
import {
  DOCUMENT_TYPE_LABELS,
  DocumentType,
  SavedDocument,
} from "../../models/document-storage.types";

interface DocumentRowProps {
  document: SavedDocument;
  onDownload: (doc: SavedDocument) => void;
  onDelete: (doc: SavedDocument) => void;
  /** True while a download URL is being fetched for this row. */
  downloading?: boolean;
}

/** One glyph per document type, so a row is identifiable without reading the label. */
const TYPE_GLYPHS: Record<DocumentType, React.ReactElement> = {
  // Stacked layers: a unit of work is a sequence of lessons.
  unit_of_work: (
    <>
      <polygon points="12 2 2 7 12 12 22 7 12 2" />
      <polyline points="2 17 12 22 22 17" />
      <polyline points="2 12 12 17 22 12" />
    </>
  ),
  // Single written page.
  lesson_plan: (
    <>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="16" y2="17" />
    </>
  ),
  // Clipboard: something a student works through.
  worksheet: (
    <>
      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2" />
      <rect x="8" y="2" width="8" height="4" rx="1" />
      <line x1="8" y1="12" x2="16" y2="12" />
      <line x1="8" y1="16" x2="13" y2="16" />
    </>
  ),
  // Presentation screen on a stand.
  slideshow: (
    <>
      <rect x="2" y="3" width="20" height="14" rx="2" />
      <line x1="8" y1="21" x2="16" y2="21" />
      <line x1="12" y1="17" x2="12" y2="21" />
    </>
  ),
};

/** A compact document-type glyph. */
const TypeIcon: React.FC<{ type: DocumentType }> = ({ type }) => (
  <span
    className={`document-row__icon document-row__icon--${type}`}
    aria-hidden="true"
  >
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {TYPE_GLYPHS[type]}
    </svg>
  </span>
);

/** Australian long-hand date ("6 June 2026"); empty string for unparseable input. */
const formatDate = (iso: string): string => {
  const date = new Date(iso);
  if (isNaN(date.getTime())) {
    return "";
  }
  return date.toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

/**
 * One row of the saved-document list.
 *
 * The row is identified by its derived, human-readable title — the document id
 * is never rendered. Download and Delete are both icon buttons sitting directly
 * on the row: the actions used to hide behind an overflow menu, which made the
 * page's whole purpose invisible to anyone who doesn't know the kebab
 * convention. Delete is guarded by a confirmation modal rather than by being
 * hard to find.
 */
export const DocumentRow: React.FC<DocumentRowProps> = ({
  document: doc,
  onDownload,
  onDelete,
  downloading = false,
}) => {
  return (
    <div className="document-row" role="row">
      <span className="document-row__main">
        <TypeIcon type={doc.document_type} />
        <span className="document-row__title">{doc.title}</span>
      </span>

      <span className="document-row__type">
        {DOCUMENT_TYPE_LABELS[doc.document_type]}
      </span>
      <span className="document-row__date">{formatDate(doc.created_at)}</span>

      <div className="document-row__actions">
        <button
          type="button"
          className="document-row__action document-row__action--download"
          onClick={() => onDownload(doc)}
          disabled={downloading}
          // Naming the document keeps a long list of identical icon buttons
          // distinguishable, and carries the in-progress state that the
          // spinner alone conveys only visually.
          aria-label={
            downloading ? `Downloading ${doc.title}` : `Download ${doc.title}`
          }
          // Native tooltip: the icon has to be learnable by hovering it.
          title={downloading ? "Downloading…" : "Download"}
        >
          {downloading ? (
            <span className="document-row__spinner" aria-hidden="true" />
          ) : (
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              aria-hidden="true"
            >
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
              <polyline points="7 10 12 15 17 10" />
              <line x1="12" y1="15" x2="12" y2="3" />
            </svg>
          )}
        </button>

        <button
          type="button"
          className="document-row__action document-row__action--delete"
          onClick={() => onDelete(doc)}
          aria-label={`Delete ${doc.title}`}
          title="Delete"
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="3 6 5 6 21 6" />
            <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
            <path d="M10 11v6" />
            <path d="M14 11v6" />
            <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
          </svg>
        </button>
      </div>
    </div>
  );
};
